import { useEffect, useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useOrg } from '@/contexts/OrgContext';

export const ImpersonationBanner = () => {
  const { org, isImpersonating, impersonationExpiresAt, stopImpersonation } = useOrg();
  const [now, setNow] = useState(Date.now());
  const [stopping, setStopping] = useState(false);

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
    } catch (error) {
      // The server session is still live — do not pretend it closed.
      toast.error(error instanceof Error ? error.message : 'Could not end the support session.');
    } finally {
      setStopping(false);
    }
  };

  useEffect(() => {
    if (!isImpersonating) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isImpersonating]);

  const remaining = useMemo(() => {
    if (!impersonationExpiresAt) return '60:00';
    const ms = Math.max(0, impersonationExpiresAt - now);
    const minutes = Math.floor(ms / 60000).toString().padStart(2, '0');
    const seconds = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, [impersonationExpiresAt, now]);

  if (!isImpersonating) return null;

  return (
    <div className="sticky top-0 z-[60] border-b border-destructive/20 bg-destructive text-destructive-foreground">
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>Viewing as {org.name} — Read-only support mode: tenant changes are disabled. Session expires in {remaining}</span>
        </div>
        <Button size="sm" variant="secondary" onClick={handleStop} disabled={stopping} className="w-full sm:w-auto">
          Stop impersonating
        </Button>
      </div>
    </div>
  );
};
